/**
 * `gcTime` for offline-durable queries — the ones the IndexedDB persister keeps
 * (`shouldDehydrateQuery` in `src/offline/persister.ts`).
 *
 * The global `gcTime` (5 min, `src/main.tsx`) garbage-collects a query's cached
 * data 5 minutes after its last observer unmounts. That is wrong for persisted
 * queries: `PersistQueryClientProvider` only re-hydrates the cache once at
 * startup, so a garbage-collected offline-durable query is unreachable until a
 * full reload — even though its data still sits in IndexedDB. Hooks for the
 * persisted set use this `gcTime` to stay GC-immune; keep this set in lockstep
 * with `shouldDehydrateQuery`.
 */
export const OFFLINE_DURABLE_GC_TIME = Infinity;

export const projectKeys = {
  all: ["projects"] as const,
  lists: () => [...projectKeys.all, "list"] as const,
  detail: (id: string) => [...projectKeys.all, "detail", id] as const,
};

export const inviteLinkKeys = {
  all: ["invite-links"] as const,
  byProject: (projectId: string) =>
    [...inviteLinkKeys.all, projectId] as const,
};

export const collectionKeys = {
  all: ["report-collections"] as const,
  list: (projectId: string) =>
    [...collectionKeys.all, "list", projectId] as const,
  /** Every report's header in a project — the counts an issue delete moves. */
  detailsByProject: (projectId: string) =>
    [...collectionKeys.all, "detail", projectId] as const,
  detail: (projectId: string, collectionId: string) =>
    [...collectionKeys.detailsByProject(projectId), collectionId] as const,
  /**
   * Every report's contents in a project. A report's rows carry the issue's
   * own fields, so any issue write makes these stale.
   */
  itemsByProject: (projectId: string) =>
    [...collectionKeys.all, "items", projectId] as const,
  items: (projectId: string, collectionId: string) =>
    [...collectionKeys.itemsByProject(projectId), collectionId] as const,
  exports: (projectId: string, collectionId: string) =>
    [...collectionKeys.all, "exports", projectId, collectionId] as const,
};

export const userKeys = {
  all: ["user"] as const,
  me: () => [...userKeys.all, "me"] as const,
};

export interface RecordFilters {
  tags?: string[];
  plan?: string[]; // plan IDs — record matches if its plan is any of these (OR)
  date_from?: string | null;
  date_to?: string | null;
  has_pin?: boolean | null;
  search?: string | null;
  hasTags?: boolean | null; // true = only tagged; false = only untagged; null/undefined = no filter
  created_by?: string[]; // user IDs — record matches if its creator is any of these
}

/**
 * Strip filter entries that mean "no filter" so the query key is canonical
 * regardless of how the caller spelled the absence.
 *
 * This matters because callers fill the filter object differently. The live
 * records-view builds `{ search: null, hasTags: null, plan: null, ... }`
 * (explicit nulls), while the pin sync in `src/offline/sync.ts` passes `{}`.
 * Stable-JSON hashing makes those two distinct keys, so a project pinned
 * from the listing view (where no `useProjectRecords` observer ever existed
 * for the empty-filter shape) ends up with cached records under the sync
 * shape that the live hook never looks for — and offline cold-boot shows
 * "Failed to load records" even though the data is sitting in IDB.
 *
 * Empty arrays, empty strings, null and undefined all normalize to omission.
 * `false` is preserved for the boolean filters because `hasTags: false` and
 * `has_pin: false` mean "only untagged" / "only unpinned" respectively.
 * Tag arrays are sorted so `{ tags: ["a", "b"] }` and `{ tags: ["b", "a"] }`
 * hash identically.
 */
function normalizeRecordFilters(filters: RecordFilters): RecordFilters {
  const out: RecordFilters = {};
  if (filters.tags && filters.tags.length > 0) {
    out.tags = [...filters.tags].sort();
  }
  if (filters.created_by && filters.created_by.length > 0) {
    out.created_by = [...filters.created_by].sort();
  }
  if (filters.plan && filters.plan.length > 0) {
    out.plan = [...filters.plan].sort();
  }
  if (filters.date_from) out.date_from = filters.date_from;
  if (filters.date_to) out.date_to = filters.date_to;
  if (filters.has_pin != null) out.has_pin = filters.has_pin;
  if (filters.search) out.search = filters.search;
  if (filters.hasTags != null) out.hasTags = filters.hasTags;
  return out;
}

export const recordKeys = {
  all: ["records"] as const,
  byProject: (projectId: string) => [...recordKeys.all, projectId] as const,
  list: (projectId: string, filters: RecordFilters = {}) =>
    [
      ...recordKeys.byProject(projectId),
      "list",
      normalizeRecordFilters(filters),
    ] as const,
  detail: (projectId: string, recordId: string) =>
    [...recordKeys.byProject(projectId), "detail", recordId] as const,
};

export const creatorKeys = {
  all: ["record-creators"] as const,
  list: (projectId: string) => [...creatorKeys.all, projectId] as const,
};

export const planKeys = {
  all: ["plans"] as const,
  byProject: (projectId: string) => [...planKeys.all, projectId] as const,
  list: (projectId: string) =>
    [...planKeys.byProject(projectId), "list"] as const,
  // Nested under byProject on purpose: every plan mutation invalidates the
  // byProject prefix, which sweeps the revision lists along with it.
  revisions: (projectId: string, planId: string) =>
    [...planKeys.byProject(projectId), "revisions", planId] as const,
};

export const reportKeys = {
  all: ["reports"] as const,
  byProject: (projectId: string) => [...reportKeys.all, projectId] as const,
  list: (projectId: string) =>
    [...reportKeys.byProject(projectId), "list"] as const,
  detail: (projectId: string, reportId: string) =>
    [...reportKeys.byProject(projectId), "detail", reportId] as const,
};

export const ISSUE_ORDERING_FIELDS = [
  "number",
  "created_by",
  "assigned_to",
  "status",
  "due_date",
  "target_date",
] as const;

export type IssueOrderingField = (typeof ISSUE_ORDERING_FIELDS)[number];
export type IssueOrdering = IssueOrderingField | `-${IssueOrderingField}`;
export const DEFAULT_ISSUE_ORDERING: IssueOrdering = "-number";

export function isIssueOrdering(value: unknown): value is IssueOrdering {
  if (typeof value !== "string") return false;
  const field = value.startsWith("-") ? value.slice(1) : value;
  return ISSUE_ORDERING_FIELDS.includes(field as IssueOrderingField);
}

export interface IssueFilters {
  status?: string[];
  assigned_to?: string[];
  created_by?: string[];
  plan?: string[];
  record?: string | null;
  due_before?: string | null;
  due_after?: string | null;
  overdue?: boolean | null;
  search?: string | null;
  ordering?: IssueOrdering;
}

/**
 * Strip filter entries that mean "no filter" so the key is canonical however
 * the caller spelled the absence — same reasoning as
 * {@link normalizeRecordFilters}, and the same trap it exists to avoid: two
 * spellings of the same view hash to two keys and neither sees the other's
 * cache.
 *
 * `overdue: false` is preserved: it means "only issues that are NOT late",
 * which is a real filter and not the absence of one.
 */
function normalizeIssueFilters(filters: IssueFilters): IssueFilters {
  const out: IssueFilters = {};
  if (filters.status && filters.status.length > 0) {
    out.status = [...filters.status].sort();
  }
  if (filters.assigned_to && filters.assigned_to.length > 0) {
    out.assigned_to = [...filters.assigned_to].sort();
  }
  if (filters.created_by && filters.created_by.length > 0) {
    out.created_by = [...filters.created_by].sort();
  }
  if (filters.plan && filters.plan.length > 0) {
    out.plan = [...filters.plan].sort();
  }
  if (filters.record) out.record = filters.record;
  if (filters.due_before) out.due_before = filters.due_before;
  if (filters.due_after) out.due_after = filters.due_after;
  if (filters.overdue != null) out.overdue = filters.overdue;
  if (filters.search) out.search = filters.search;
  if (filters.ordering) out.ordering = filters.ordering;
  return out;
}

export const issueKeys = {
  all: ["issues"] as const,
  byProject: (projectId: string) => [...issueKeys.all, projectId] as const,
  list: (projectId: string, filters: IssueFilters = {}) =>
    [
      ...issueKeys.byProject(projectId),
      "list",
      normalizeIssueFilters(filters),
    ] as const,
  detail: (projectId: string, issueId: string) =>
    [...issueKeys.byProject(projectId), "detail", issueId] as const,
  // The issue's own read of its record: photos and pin, for anyone who can see
  // the issue. Not `recordKeys.detail` — that is the records endpoint, which
  // needs record permissions and carries the whole record.
  record: (projectId: string, issueId: string) =>
    [...issueKeys.byProject(projectId), "record", issueId] as const,
  // Nested under byProject on purpose: posting an update invalidates the
  // byProject prefix, which sweeps the counts along with the list. The counts
  // and the list must never disagree — a header saying 3 above a list of 4 is
  // worse than either being briefly stale.
  summary: (projectId: string, filters: IssueFilters = {}) =>
    [
      ...issueKeys.byProject(projectId),
      "summary",
      normalizeIssueFilters(filters),
    ] as const,
};

export const roleKeys = {
  all: ["roles"] as const,
  defaults: () => [...roleKeys.all, "defaults"] as const,
};

export const autodeskKeys = {
  all: ["autodesk"] as const,
  connection: (projectId: string) =>
    [...autodeskKeys.all, "connection", projectId] as const,
  robot: (projectId: string) =>
    [...autodeskKeys.all, "robot", projectId] as const,
  formaProjects: (projectId: string) =>
    [...autodeskKeys.all, "forma-projects", projectId] as const,
  recordPush: (projectId: string, recordId: string) =>
    [...autodeskKeys.all, "record-push", projectId, recordId] as const,
};

export const issueInviteKeys = {
  all: ["issue-invites"] as const,
  // Asked only while signed out — signed in, the page joins instead — so the
  // answer never depends on who is asking.
  preview: (token: string) => [...issueInviteKeys.all, token, "preview"] as const,
};

export const shareKeys = {
  all: ["share"] as const,
  byToken: (token: string) => [...shareKeys.all, token] as const,
  // Keyed by viewer: the backend answers `is_authenticated` / `is_member` from
  // the request's bearer, so an anonymous answer must never serve a signed-in
  // visitor (or vice versa) — see [[share-page.spec]].
  metadata: (token: string, viewerId: string | null) =>
    [...shareKeys.byToken(token), "metadata", viewerId ?? "anonymous"] as const,
  // Reuses the authenticated grid's RecordFilters shape + normalization so the
  // public share grid filters/keys behave identically — see [[share-page.spec]].
  records: (token: string, filters: RecordFilters = {}) =>
    [
      ...shareKeys.byToken(token),
      "records",
      normalizeRecordFilters(filters),
    ] as const,
};
